import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Lock, ExternalLink, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface KBSearchResult {
  id: string;
  title: string;
  description: string;
  channel: string;
  creator: string;
  isMinted: boolean;
  link: string;
  hasAccess?: boolean;
}

interface KBSearchResultsProps {
  results: KBSearchResult[];
  onSelect?: (kb: KBSearchResult) => void;
  onRequestAccess?: (kb: KBSearchResult) => void;
}

export const KBSearchResults = ({ 
  results, 
  onSelect,
  onRequestAccess 
}: KBSearchResultsProps) => {
  const navigate = useNavigate();

  const handleViewDetails = (kb: KBSearchResult) => {
    if (onSelect) {
      onSelect(kb);
    }
    navigate(`/knowledge-base/${kb.id}`);
  };

  const handleRequestAccess = (kb: KBSearchResult) => {
    if (onRequestAccess) {
      onRequestAccess(kb);
    }
    navigate(`/marketplace?kb=${kb.id}`);
  };

  if (!results || results.length === 0) {
    return null;
  }

  return (
    <div className="my-3 rounded-lg border border-border bg-card/50 overflow-hidden">
      <div className="px-3 py-2 bg-muted/50 border-b border-border flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium text-foreground">
          Found {results.length} Knowledge Base{results.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      <ScrollArea className="max-h-[300px]">
        <div className="divide-y divide-border">
          {results.map((kb) => (
            <div 
              key={kb.id} 
              className="p-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm text-foreground truncate">
                      {kb.title}
                    </h4>
                    {kb.isMinted && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        ▰ Minted
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                    {kb.description}
                  </p>
                  
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{kb.channel}</span>
                    <span>•</span>
                    <span>by {kb.creator}</span>
                  </div>
                </div>
                
                <div className="shrink-0">
                  {kb.hasAccess !== false ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5"
                      onClick={() => handleViewDetails(kb)}
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      View Details
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 gap-1.5"
                      onClick={() => handleRequestAccess(kb)}
                    >
                      <Lock className="h-3.5 w-3.5" />
                      Request Access
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

// Helper to parse KB results from Giblet response
export const parseKBResultsFromResponse = (content: string): KBSearchResult[] | null => {
  // Look for structured KB results in the response
  // Format: KB_RESULTS: [{"id": "...", "title": "...", ...}]
  const kbResultsMatch = content.match(/KB_RESULTS:\s*(\[[\s\S]*?\])/);
  if (kbResultsMatch) {
    try {
      return JSON.parse(kbResultsMatch[1]);
    } catch {
      return null;
    }
  }
  return null;
};
